import React, { useState, useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Modal from './Modal';
import { toast } from 'react-toastify';
import { interviewsAPI } from '../../services/api';
import { getInterviewReportFileError, INTERVIEW_REPORT_ACCEPT } from '../../utils/uploadLimits';

/**
 * France campus: pass/fail requires interview comment + report (new upload or existing report on row).
 */
export default function FranceInterviewOutcomeModal({ open, interview, targetOutcome, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open || !interview) return;
    setComment(String(interview.interview_comment || '').trim() ? interview.interview_comment : '');
    setFile(null);
    if (fileRef.current) fileRef.current.value = '';
  }, [open, interview]);

  const submit = async () => {
    if (!interview || !targetOutcome) return;
    const c = comment.trim();
    if (!c) {
      toast.error(t('franceInterview.commentRequired'));
      return;
    }
    const mustUploadFile = !interview.report_file_path;
    if (mustUploadFile && !file) {
      toast.error(t('franceInterview.reportRequired'));
      return;
    }
    if (file) {
      const err = getInterviewReportFileError(file);
      if (err) {
        toast.error(err);
        return;
      }
    }
    try {
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('interview_comment', c);
        await interviewsAPI.uploadReport(interview.interview_id, fd);
      } else {
        await interviewsAPI.update(interview.interview_id, { interview_comment: c });
      }
      await interviewsAPI.update(interview.interview_id, { outcome: targetOutcome });
      toast.success(t('franceInterview.markedAs', { outcome: targetOutcome.toUpperCase() }));
      onClose();
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.message || t('franceInterview.updateFailed'));
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={() => {
        onClose();
        setComment('');
        setFile(null);
        if (fileRef.current) fileRef.current.value = '';
      }}
      title={
        targetOutcome
          ? t('franceInterview.modalTitle', {
              outcome: targetOutcome === 'pass' ? t('franceInterview.pass') : t('franceInterview.fail'),
            })
          : ''
      }
    >
      {interview && (
        <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            <Trans i18nKey="franceInterview.desc" components={{ strong: <strong /> }} />
          </p>
          <div className="form-group">
            <label>
              {t('franceInterview.commentLabel')} <span style={{ color: 'var(--danger)' }}>*</span>
            </label>
            <textarea
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t('franceInterview.commentPlaceholder')}
            />
          </div>
          <div className="form-group">
            <label>
              {t('franceInterview.reportLabel')}{' '}
              {!interview.report_file_path ? (
                <span style={{ color: 'var(--danger)' }}>*</span>
              ) : (
                <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>{t('franceInterview.replaceExisting')}</span>
              )}
            </label>
            <input
              ref={fileRef}
              type="file"
              accept={INTERVIEW_REPORT_ACCEPT}
              onChange={(e) => {
                const inputEl = e.target;
                const f = inputEl.files?.[0];
                if (!f) {
                  setFile(null);
                  return;
                }
                const err = getInterviewReportFileError(f);
                if (err) {
                  toast.error(err);
                  inputEl.value = '';
                  setFile(null);
                  return;
                }
                setFile(f);
              }}
            />
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{t('franceInterview.reportHint')}</p>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => {
                onClose();
                setComment('');
                setFile(null);
                if (fileRef.current) fileRef.current.value = '';
              }}
            >
              {t('common.cancel')}
            </button>
            <button type="button" className="btn btn-primary" onClick={submit}>
              {t('franceInterview.saveResult')}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
